import React, {useMemo, Suspense, lazy} from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import SGBody from 'components/Typography/SGBody';
import Skeleton from '@material-ui/lab/Skeleton';
import Accordion from 'features/POSRegisters/components/Accordion';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import Device from 'features/POSRegisters/interfaces/Device';
import SeverityEnum from 'features/POSRegisters/interfaces/Severity';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import {
  ReceiptPrinterScanDetails,
  BarCodeScanDetails,
  CashDrawerScanDetails,
} from 'features/POSRegisters/interfaces/ScanDetails';
import {withAlerts} from 'features/POSRegisters/POSTable/Registers/components/TableWithAlerts';
import {AlertScanSubType} from 'features/POSRegisters/interfaces/AlertInfo';

const TableWithAlerts = lazy(
  () => import('features/POSRegisters/POSTable/Registers/components/TableWithAlerts')
);

//#region styles
const StyledContainer = styled(Grid)`
  padding: 8px;
  gap: 8px;

  .MuiTableCell-root.MuiTableCell-head {
    padding-top: 4px;
    padding-bottom: 0;
  }

  .MuiTableCell-root.MuiTableCell-head span {
    font-size: 12px;
  }

  .MuiTableCell-root.MuiTableCell-body p {
    font-size: 16px;
  }

  > .MuiPaper-root {
    background: transparent;
  }
`;

const StyledSkeleton = styled(Skeleton)`
  height: 300px;
  background-color: ${props => props.theme.text}0D;
`;

const StyledAccordion = styled(Accordion)`
  padding: 0;

  > .MuiAccordionSummary-root {
    height: 24px;
    padding: 0 8px;
    font-size: 16px;
    font-weight: 700;
    color: ${props => props.theme.text};
    text-transform: uppercase;
  }

  &.neutral > .MuiAccordionSummary-root {
    background: ${props => props.theme.disabledStateBlue}33;
  }

  &.transparent > .MuiAccordionSummary-root {
    background: transparent;
    padding: 8px 0 0;
  }
`;

const StyledHeader = styled(Grid)`
  display: flex;
  align-items: center;
  gap: 4px;
  max-height: 24px;
  padding: 4px 8px;
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.theme.text};
  text-transform: uppercase;
  background: ${props => props.theme.disabledStateBlue}33;

  &.transparent {
    background: transparent;
    padding: 8px 0 0;
  }
`;

const StyledTableContainer = styled(Grid)`
  padding: 0 32px;
`;

//#endregion styles

const connectivityColumns = [
  {
    id: 'deviceName',
    label: 'DEVICE NAME',
    width: '10rem',
  },
  {
    id: 'pingStatus',
    label: 'PING STATUS',
    width: '6rem',
    format: (value: string) => <SGBody variant={'b4'}>{value ?? 'Failure'}</SGBody>,
  },
  {
    id: 'ipAddress',
    label: 'IP ADDRESS',
    width: '8rem',
  },
  {
    id: 'macAddress',
    label: 'MAC ADDRESS',
    width: '10rem',
  },
  {
    id: 'vlanNumber',
    label: 'VLAN',
    width: '6rem',
  },
  {
    id: 'switchId',
    label: 'Switch ID',
    width: '100px',
  },
  {
    id: 'switchNumber',
    label: 'Switch Number',
    width: '102px',
  },
  {
    id: 'portNumber',
    label: 'Port Number',
    width: '100px',
  },
];

const checkInColumns = [
  {
    id: 'deviceName',
    label: 'Device Name',
    width: '10rem',
  },
  {
    id: 'printerFirstSeenDate',
    label: 'First Seen Date',
    width: '250px',
  },
  {
    id: 'printerLastSeenDate',
    label: 'Last Seen Date',
    width: '250px',
  },
];

const hardwareColumns = [
  {
    id: 'deviceName',
    label: 'DEVICE NAME',
    width: '10rem',
  },
  {
    id: 'manufacturer',
    label: 'MANUFACTURER',
    width: '8rem',
  },
  {
    id: 'model',
    label: 'MODEL',
    width: '8rem',
  },
  {
    id: 'serialNumber',
    label: 'SERIAL NUMBER',
    width: '8rem',
  },
  {
    id: 'printerId',
    label: 'Printer Id',
    width: '400px',
  },
  {
    id: 'associatedDevice',
    label: 'ASSOCIATED DEVICE',
    width: '10rem',
  },
];

const barcodeScannerColumns = [
  {
    id: 'associatedDevice',
    label: 'DEVICE NAME',
    width: '10rem',
  },
  {
    id: 'deviceName',
    label: 'PRINTER NAME',
    width: '8rem',
  },
  {
    id: 'manufacturer',
    label: 'MANUFACTURER',
    width: '8rem',
  },
  {
    id: 'model',
    label: 'MODEL',
    width: '8rem',
  },
  {
    id: 'serialNumber',
    label: 'SERIAL NUMBER',
    width: '8rem',
  },
];

const cashDrawerColumns = [
  {
    id: 'tillNumber',
    label: 'Till Number',
    width: '10rem',
  },
  {
    id: 'signalConfiguration',
    label: 'Signal Configuration',
    width: '8rem',
  },
];

interface WithConditionalWrapperProps {
  children: React.ReactNode;
  className?: string;
  title: string;
  wrapper: 'accordion' | 'default';
  arrowPlacement?: 'start' | 'end';
  severity?: SeverityEnum;
}

function WithConditionalWrapper(props: WithConditionalWrapperProps) {
  const {children, className, title, wrapper, arrowPlacement, severity} = props;
  switch (wrapper) {
    case 'accordion':
      return (
        <StyledAccordion
          className={className}
          title={title}
          arrowPlacement={arrowPlacement}
          severity={severity}
          expanded={true}
        >
          {children}
        </StyledAccordion>
      );
    default:
      return (
        <Grid container>
          <StyledHeader className={className} item xs={12}>
            {severity && <SeverityBadge severity={severity} size={0.75} />}
            {title}
          </StyledHeader>
          <Grid item xs={12}>
            {children}
          </Grid>
        </Grid>
      );
  }
}

interface ReceiptPrintersProps {
  receiptPrinters: Device<ReceiptPrinterScanDetails>[];
  barcodeScanners: Device<BarCodeScanDetails>[];
  cashDrawers: Device<CashDrawerScanDetails>[];
}

export default function ReceiptPrinters({
  receiptPrinters,
  barcodeScanners,
  cashDrawers,
}: ReceiptPrintersProps) {
  const wrapper = useMemo(() => (receiptPrinters.length > 5 ? 'accordion' : 'default'), [
    receiptPrinters,
  ]);
  return (
    <StyledContainer container>
      <WithConditionalWrapper className="neutral" title="Connectivity" wrapper={wrapper}>
        <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
          <StyledTableContainer>
            <TableWithAlerts
              paginationText={'Devices'}
              columns={connectivityColumns}
              data={withAlerts(receiptPrinters, AlertScanSubType.CONNECTIVITY)}
              noDataText={'No devices were found.'}
              loaded={true}
              disablePagination={wrapper === 'default'}
            />
          </StyledTableContainer>
        </Suspense>
      </WithConditionalWrapper>

      <WithConditionalWrapper className="neutral" title="Check-In Status" wrapper={wrapper}>
        <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
          <StyledTableContainer>
            <TableWithAlerts
              paginationText={'devices'}
              columns={checkInColumns}
              data={withAlerts(receiptPrinters, AlertScanSubType.CHECK_IN_STATUS)}
              noDataText={'No devices were found.'}
              loaded={true}
              disablePagination={wrapper === 'default'}
            />
          </StyledTableContainer>
        </Suspense>
      </WithConditionalWrapper>

      <WithConditionalWrapper className="neutral" title="Information" wrapper={wrapper}>
        <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
          <StyledTableContainer>
            <WithConditionalWrapper
              className="transparent"
              title="HARDWARE"
              arrowPlacement="start"
              wrapper={wrapper}
            >
              <TableWithAlerts
                paginationText={'Devices'}
                columns={hardwareColumns}
                data={withAlerts(receiptPrinters, AlertScanSubType.HARDWARE)}
                noDataText={'No devices were found.'}
                loaded={true}
                disablePagination={wrapper === 'default'}
              />
            </WithConditionalWrapper>
          </StyledTableContainer>
        </Suspense>
      </WithConditionalWrapper>

      {barcodeScanners.length > 0 && (
        <WithConditionalWrapper
          className="neutral"
          title="BARCODE SCANNER"
          wrapper={wrapper}
          severity={getPriorityAlert(barcodeScanners)}
        >
          <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
            <StyledTableContainer>
              <TableWithAlerts
                paginationText={'Barcode Scanners'}
                columns={barcodeScannerColumns}
                data={withAlerts(barcodeScanners)}
                noDataText={'No barcode scanners were found.'}
                loaded={true}
                disablePagination={wrapper === 'default'}
              />
            </StyledTableContainer>
          </Suspense>
        </WithConditionalWrapper>
      )}

      {cashDrawers.length > 0 && (
        <WithConditionalWrapper
          className="neutral"
          title="CASH DRAWER"
          wrapper={wrapper}
          severity={getPriorityAlert(cashDrawers)}
        >
          <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
            <StyledTableContainer>
              <TableWithAlerts
                paginationText={'Cash Drawers'}
                columns={cashDrawerColumns}
                data={withAlerts(cashDrawers)}
                noDataText={'No cash drawers were found.'}
                loaded={true}
                disablePagination={wrapper === 'default'}
              />
            </StyledTableContainer>
          </Suspense>
        </WithConditionalWrapper>
      )}
    </StyledContainer>
  );
}
